import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Ticker from "@/components/feature/Ticker";
import ArticleCard from "@/components/feature/ArticleCard";
import FaqAccordion from "@/components/feature/FaqAccordion";
import HeroSection from "@/pages/home/components/HeroSection";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { articleCategories, gossipArticles, morningArticles } from "@/mocks/articles";
import { faqs, siteInfo, testimonials } from "@/mocks/site";

const categoryIcons: Record<string, string> = {
  剧集观察: "ri-movie-2-line",
  综艺幕后: "ri-mic-line",
  音乐现场: "ri-music-2-line",
  行业风向: "ri-compass-3-line",
  平台生态: "ri-dashboard-3-line",
};

export default function Home() {
  const lead = gossipArticles[0];
  const briefs = morningArticles.slice(0, 4);
  const morningList = morningArticles.slice(0, 4);
  const gossipList = gossipArticles.slice(1, 7);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: siteInfo.name,
      url: "https://mynami.cn/",
      description: siteInfo.description,
      inLanguage: "zh-CN",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://mynami.cn/gossip?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteInfo.name,
      url: "https://mynami.cn/",
      description: siteInfo.description,
      email: siteInfo.email,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.slice(0, 5).map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="黑料每日 | 早报与一瓜，每日精选必看合集"
        description="黑料每日每天固定更新早报与一瓜，精选当日娱乐行业热门话题与幕后观察，合集整理方便快速浏览，点开即可获取完整瓜单。"
        keywords="黑料每日,黑料每日早报,黑料每日一瓜,黑料每日精选,黑料每日必看"
        canonicalPath="/"
        jsonLd={jsonLd}
      />
      <Ticker />
      <HeroSection lead={lead} briefs={briefs} />

      <section className="px-4 md:px-8 mt-14 md:mt-20" aria-labelledby="morning-heading">
        <SectionHeading
          id="morning-heading"
          eyebrow="MORNING BRIEF"
          title="每日早报"
          desc="用两分钟读完昨天到今晨值得关注的行业动态，一天的开场白就该这么利落。"
          morePath="/morning"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {morningList.map((item) => (
            <ArticleCard key={item.id} article={item} variant="compact" />
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 mt-14 md:mt-20" aria-labelledby="gossip-heading">
        <SectionHeading
          id="gossip-heading"
          eyebrow="DAILY STORY"
          title="每日一瓜"
          desc="每天挑一个话题，把它背后的机制讲清楚，而不是只抛结论。"
          morePath="/gossip"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {gossipList.map((item) => (
            <ArticleCard key={item.id} article={item} variant="standard" />
          ))}
        </div>
      </section>

      <section className="mt-14 md:mt-20 bg-background-100 border-y border-background-200 py-12 md:py-16" aria-labelledby="category-heading">
        <div className="px-4 md:px-8">
          <SectionHeading
            id="category-heading"
            eyebrow="CATEGORIES"
            title="按分类找瓜"
            desc="五大固定栏目覆盖影视、综艺、音乐与平台生态，想追哪条线就点哪条。"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {articleCategories.map((category) => (
              <Link
                key={category}
                to={`/gossip?category=${encodeURIComponent(category)}`}
                className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors">
                  <i className={`${categoryIcons[category]} text-xl`} aria-hidden="true" />
                </span>
                <span className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {category}
                </span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  进入栏目
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 mt-14 md:mt-20" aria-labelledby="voice-heading">
        <SectionHeading
          id="voice-heading"
          eyebrow="READERS"
          title="读者怎么说"
          desc="来自通勤党、从业者与内容运营的真实使用反馈。"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {testimonials.map((item) => (
            <figure key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 flex flex-col">
              <i className="ri-double-quotes-l text-2xl text-accent-400 mb-3" aria-hidden="true" />
              <blockquote className="text-sm text-foreground-700 leading-relaxed flex-1">
                {item.quote}
              </blockquote>
              <figcaption className="mt-4 pt-4 border-t border-background-200 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-heading font-semibold">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                  <span className="block text-[11px] text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 mt-14 md:mt-20" aria-labelledby="faq-heading">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <SectionHeading id="faq-heading" eyebrow="FAQ" title="常见问题" desc="关于更新节奏、内容筛选与合作的常见疑问。" />
            <Link
              to="/faq"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              查看全部问题
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
          <div className="lg:col-span-2">
            <FaqAccordion items={faqs.slice(0, 4)} />
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 mt-14 md:mt-20" aria-labelledby="cta-heading">
        <div className="relative overflow-hidden rounded-lg bg-secondary-900 px-6 py-10 md:px-12 md:py-14">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h2 id="cta-heading" className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                别再到处搜瓜单，在这里一次看完
              </h2>
              <p className="text-sm text-secondary-100/85 leading-relaxed">
                早报 07:30、一瓜 20:00，固定栏目准时送达。想报料或合作，也可以直接联系我们。
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/morning"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 px-5 py-3 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-sun-line" aria-hidden="true" />
                看今日早报
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md border border-secondary-600 text-background-50 px-5 py-3 text-sm font-medium hover:bg-secondary-800 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-send-line" aria-hidden="true" />
                联系我们
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}